import { AuthToken } from '@cobliteam/api-lib';
import { Location } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import React, { Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { storybookSvg, tailwindSvg } from './assets';
import DeveloperToolsDialog from './components/DeveloperToolsDialog';
import TailwindIndicator from './components/TailwindIndicator';
import { copyJwtToken } from './lib/clipboard';
import { type DevToolsPosition, getMenuPlacement, getPositionClasses, readStoredPosition, writeStoredPosition } from './lib/devToolsPosition';

const SHORTCUT_KEY = 'd';

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

const getShortcutLabel = () => {
  const isApple = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.userAgent);
  return isApple ? '⌘⇧D' : 'Ctrl+Shift+D';
};

const DeveloperTools = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showTailwindIndicator, setShowTailwindIndicator] = useState(false);
  const [showReactQueryDevtools, setShowReactQueryDevtools] = useState(false);
  // Lazy initializer, not an effect: reading in an effect made the toolbar paint
  // at the default placement and then jump to the stored one on every load.
  const [position, setPosition] = useState<DevToolsPosition>(readStoredPosition);

  const history = useHistory();
  const { getUser, getSession } = useUser();

  // Genuine external-system subscription (window keydown), not derived state.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() === SHORTCUT_KEY && event.shiftKey && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setIsMenuOpen((open) => !open);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleChangePosition = (next: DevToolsPosition) => {
    setPosition(next);
    writeStoredPosition(next);
  };

  const handleCopyToken = () => {
    void copyJwtToken(getSession().authenticationToken ?? localStorage.getItem(AuthToken.ACCESS_TOKEN));
  };

  const isLocalhost = typeof window !== 'undefined' && window.location.hostname.includes('localhost');
  const isPreview = typeof window !== 'undefined' && window.location.hostname.includes('preview');
  const isCobler = getUser().role === 'cobler';

  // `NODE_ENV === 'test'` only covers unit tests. Playwright drives a real dev/preview
  // server on localhost, where NODE_ENV is development/production — without the
  // webdriver check, opening the localhost gate would float this overlay over every
  // e2e run and let it swallow clicks. Automated browsers set navigator.webdriver.
  const isAutomated = process.env.NODE_ENV === 'test' || (typeof navigator !== 'undefined' && navigator.webdriver === true);

  // Localhost is open so a fresh clone shows the toolbar with zero setup (it used
  // to need a per-user `developerTools` flag). Preview still requires a Cobler so
  // client-facing preview links stay clean.
  if (isAutomated || !(isLocalhost || (isPreview && isCobler))) return null;

  const { side, align } = getMenuPlacement(position);
  const shortcutLabel = getShortcutLabel();

  return (
    <>
      {showReactQueryDevtools && (
        <Suspense>
          <ReactQueryDevtoolsProduction initialIsOpen />
        </Suspense>
      )}

      <div className={cn('fixed z-devtools flex w-fit items-start gap-2', getPositionClasses(position))}>
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              type='button'
              aria-label={`Ferramentas de desenvolvimento (${shortcutLabel})`}
              className='flex h-8 items-center gap-1.5 rounded-lg border border-gray-6 bg-gray-12 pr-2.5 pl-2 font-medium text-white text-xs shadow-sm transition-colors hover:bg-gray-11 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2'
            >
              <Icon name='settings-2' size='sm' />
              Dev
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content side={side} align={align} className='w-64'>
            <DropdownMenu.Header className='flex items-center justify-between gap-2'>
              Ferramentas
              <kbd className='rounded border border-gray-6 bg-gray-2 px-1.5 py-0.5 font-mono text-[10px] text-gray-11'>{shortcutLabel}</kbd>
            </DropdownMenu.Header>

            <DropdownMenu.Group>
              {/* `onClick`, not `onSelect`: DropdownMenu.Item swallows a consumer
                  `onSelect` and substitutes its own for the `keepOpen` behaviour. */}
              <DropdownMenu.Item onClick={() => setIsDialogOpen(true)}>
                <DropdownMenu.Icon icon={<Icon name='wrench' />} />
                <DropdownMenu.Label>Developer Tools</DropdownMenu.Label>
              </DropdownMenu.Item>
            </DropdownMenu.Group>

            <DropdownMenu.Separator />
            <DropdownMenu.Header>Design system</DropdownMenu.Header>
            <DropdownMenu.Group>
              <DropdownMenu.Item onClick={() => history.push(Location.COLIB_SHOWCASE)}>
                <DropdownMenu.Icon icon={<Icon name='palette' />} />
                <DropdownMenu.Label>Colib UI</DropdownMenu.Label>
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => window.open('https://cobliteam.github.io/colib-ui', '_blank', 'noopener,noreferrer')}>
                <DropdownMenu.Icon icon={<img src={storybookSvg} alt='' className='size-4' />} />
                <DropdownMenu.Label>
                  Storybook
                  <Icon name='external-link' size='xs' className='ml-auto text-gray-9' />
                </DropdownMenu.Label>
              </DropdownMenu.Item>
            </DropdownMenu.Group>

            <DropdownMenu.Separator />
            <DropdownMenu.Header>Inspecionar</DropdownMenu.Header>
            <DropdownMenu.Group>
              <DropdownMenu.CheckboxItem checked={showReactQueryDevtools} onCheckedChange={setShowReactQueryDevtools}>
                <DropdownMenu.Label>
                  <Icon name='database' size='sm' className='text-gray-11' />
                  React Query Devtools
                </DropdownMenu.Label>
              </DropdownMenu.CheckboxItem>
              <DropdownMenu.CheckboxItem checked={showTailwindIndicator} onCheckedChange={setShowTailwindIndicator}>
                <DropdownMenu.Label>
                  <img src={tailwindSvg} alt='' className='size-4' />
                  Breakpoints
                </DropdownMenu.Label>
              </DropdownMenu.CheckboxItem>
            </DropdownMenu.Group>

            <DropdownMenu.Separator />
            <DropdownMenu.Group>
              <DropdownMenu.Item onClick={handleCopyToken}>
                <DropdownMenu.Icon icon={<Icon name='copy' />} />
                <DropdownMenu.Label>Copiar JWT Token</DropdownMenu.Label>
              </DropdownMenu.Item>
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu>

        {showTailwindIndicator && <TailwindIndicator onClose={() => setShowTailwindIndicator(false)} />}
      </div>

      <DeveloperToolsDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} position={position} onPositionChange={handleChangePosition} />
    </>
  );
};

export default DeveloperTools;
